import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorMessage } from '../components/common';
import { SocialLogin } from '../components/SocialLogin';
import { externalErrorMessage } from '../lib/externalAuth';
import { useDocumentTitle } from '../hooks/useApi';
import { api, ApiError } from '../lib/api';
import type { SsoDiscovery } from '../lib/organizations';
import { useAuth } from '../lib/useAuth';
import './auth.css';

export function LoginPage() {
  useDocumentTitle('Entrar');

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const destination = (location.state as { from?: string } | null)?.from ?? '/cuenta';

  /*
   * El rodeo por Google o Apple no puede volver con un mensaje: vuelve con una redirección. El
   * motivo llega en la query como código, y aquí se traduce. El texto vive en la SPA y no en la
   * URL para que no acabe escrito en el historial ni en los registros del servidor.
   */
  const [params] = useSearchParams();
  const externalError = externalErrorMessage(params.get('error'));

  /*
   * Acceso con la cuenta de la empresa (Entra ID, ADR-014). Con el correo escrito se pregunta
   * a la API si ese dominio entra por SSO. Si es obligatorio —o si la persona no ha puesto
   * contraseña— se la manda al login de su empresa, con el correo como pista para que
   * Microsoft no se lo vuelva a pedir.
   */
  function goToCompanyLogin() {
    const query = new URLSearchParams({ email: email.trim(), returnUrl: destination });
    window.location.assign(`${api.baseUrl}/auth/external/entra/start?${query.toString()}`);
  }

  async function discover(): Promise<SsoDiscovery> {
    try {
      return await api.post<SsoDiscovery>('/auth/sso/discover', { email: email.trim() });
    } catch {
      // Si la consulta falla se sigue con la contraseña: el servidor vuelve a comprobarlo y,
      // si el SSO era obligatorio, el login responde «auth.sso_required».
      return { sso: false, enforced: false };
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      if (email.includes('@')) {
        const sso = await discover();
        if (sso.enforced || (sso.sso && password === '')) {
          goToCompanyLogin();
          return;
        }
      }

      await login(email, password);
      navigate(destination, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'auth.sso_required') {
        goToCompanyLogin();
        return;
      }

      setError(caught instanceof ApiError ? caught.message : 'No hemos podido iniciar sesión.');
      setBusy(false);
    }
  }

  async function companyLogin() {
    setError(null);

    if (!email.includes('@')) {
      setError('Escribe primero tu correo de empresa.');
      return;
    }

    setBusy(true);
    const sso = await discover();

    if (sso.sso) {
      goToCompanyLogin();
      return;
    }

    setBusy(false);
    setError('Tu empresa no tiene acceso con cuenta corporativa. Entra con tu contraseña.');
  }

  return (
    <div className="auth container">
      <div className="auth__card card">
        <h1>Entrar</h1>

        {(error || externalError) && <ErrorMessage>{error ?? externalError}</ErrorMessage>}

        <form onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              required
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="login-password">Contraseña</label>
            <input
              id="login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          <button type="submit" className="btn btn--primary auth__submit" disabled={busy}>
            {busy ? 'Entrando…' : 'Entrar'}
          </button>
          <button type="button" className="btn btn--ghost auth__submit" disabled={busy} onClick={companyLogin}>
            Entrar con tu cuenta de empresa
          </button>
        </form>

        <SocialLogin returnUrl={destination} />

        <p className="auth__links">
          <Link to="/recuperar">He olvidado la contraseña</Link>
        </p>
        <p className="auth__links muted">
          ¿Aún no tienes cuenta? <Link to="/registro">Crear una</Link>
        </p>
      </div>
    </div>
  );
}
